import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X, FileText, AlertTriangle, ExternalLink } from "lucide-react";

// Field catalog per document type
export const PDF_FIELD_CATALOG = {
  emergency_consent: {
    label: "Emergency Contact / Parental Consent",
    fields: [
      { name: "child.dateOfBirth", holds: "Child's date of birth" },
      { name: "child.address", holds: "Child's home address (full line)" },
      { name: "child.mother.name", holds: "Mother / Parent 1 full name" },
      { name: "child.mother.homePhone", holds: "Mother home phone" },
      { name: "child.mother.cellPhone", holds: "Mother cell — used for Parent 1 phone slot" },
      { name: "child.mother.homeAddress", holds: "Mother home address" },
      { name: "child.mother.businessName", holds: "Mother employer" },
      { name: "child.mother.businessPhone", holds: "Mother work phone" },
      { name: "child.mother.businessAddress", holds: "Mother work address" },
      { name: "child.father.name", holds: "Father / Parent 2 full name" },
      { name: "child.father.homePhone", holds: "Father home phone" },
      { name: "child.father.cellPhone", holds: "Father cell phone" },
      { name: "child.father.homeAddress", holds: "Father home address" },
      { name: "child.father.businessName", holds: "Father employer" },
      { name: "child.father.businessPhone", holds: "Father work phone" },
      { name: "child.father.businessAddress", holds: "Father work address" },
      { name: "child.emergencyContacts[0].name", holds: "Emergency contact #1 name" },
      { name: "child.emergencyContacts[0].phone", holds: "Emergency contact #1 phone" },
      { name: "child.emergencyContacts[1].name", holds: "Emergency contact #2 name" },
      { name: "child.emergencyContacts[1].phone", holds: "Emergency contact #2 phone" },
      { name: "child.emergencyContacts[2].name", holds: "Emergency contact #3 name" },
      { name: "child.emergencyContacts[2].phone", holds: "Emergency contact #3 phone" },
      { name: "child.authorizedPickup[0].nameAndAddress", holds: "Authorized pickup #1 (name + address)" },
      { name: "child.authorizedPickup[0].phone", holds: "Authorized pickup #1 phone" },
      { name: "child.authorizedPickup[1].nameAndAddress", holds: "Authorized pickup #2" },
      { name: "child.authorizedPickup[1].phone", holds: "Authorized pickup #2 phone" },
      { name: "child.authorizedPickup[2].nameAndAddress", holds: "Authorized pickup #3" },
      { name: "child.authorizedPickup[2].phone", holds: "Authorized pickup #3 phone" },
      { name: "child.physician.name", holds: "Doctor's full name" },
      { name: "child.physician.phone", holds: "Doctor's phone" },
      { name: "child.physician.address", holds: "Doctor's office address" },
      { name: "child.specialDisabilities", holds: "Documented disabilities" },
      { name: "child.allergies", holds: "Allergy list" },
      { name: "child.medicalEmergencyInfo", holds: "Emergency medical info / instructions" },
      { name: "child.medicationSpecialConditions", holds: "Current medications" },
      { name: "child.specialNeedsNotes", holds: "Additional special needs notes" },
      { name: "child.healthInsuranceCarrier", holds: "Insurance provider name" },
      { name: "child.healthInsurancePolicyNumber", holds: "Policy number" },
      { name: "child.consents.emergencyMedicalCare", holds: "Signature/initials — Emergency Medical Care" },
      { name: "child.consents.firstAid", holds: "Signature/initials — First Aid" },
      { name: "child.consents.walksAndTrips", holds: "Signature/initials — Walks & Trips" },
      { name: "child.consents.medicationsAndDiet", holds: "Signature/initials — Medications & Diet" },
      { name: "child.consents.transportation", holds: "Signature/initials — Transportation" },
      { name: "child.consents.swimmingWading", holds: "Signature/initials — Swimming / Wading" },
      { name: "child.forms.emergencyContact.signedDate", holds: "Date signed at original enrollment" },
      { name: "child.forms.emergencyContact.review1Date", holds: "Date of 1st annual review re-signature" },
      { name: "child.forms.emergencyContact.review2Date", holds: "Date of 2nd annual review re-signature" },
    ],
    notes: [
      "Newest of signedDate / review1Date / review2Date becomes the compliance card 'Date on Form' and resets the 6-month EC review clock.",
      "Mother/Father auto-fill Parent 1 & Parent 2 slots with relationship label.",
      "Authorized pickups stage in a Pending Pickup Sync review card — director approves before saving to Guardians.",
      "Emergency contacts populate the 'Emergency Contacts (Call Only)' section on Edit Child.",
      "Physician block powers the Physician section on Edit Child AND the Excursion Info Sheet print.",
    ],
  },
  enrollment_info: {
    label: "Enrollment Information Sheet",
    fields: [
      { name: "child.dateOfBirth", holds: "Child's DOB" },
      { name: "child.address.street", holds: "Street address" },
      { name: "child.address.city", holds: "City" },
      { name: "child.address.state", holds: "State" },
      { name: "child.address.zip", holds: "ZIP code" },
      { name: "child.parents[0].name", holds: "Parent 1 name" },
      { name: "child.parents[0].phone", holds: "Parent 1 home phone" },
      { name: "child.parents[0].cell", holds: "Parent 1 cell phone" },
      { name: "child.parents[1].name", holds: "Parent 2 name" },
      { name: "child.parents[1].phone", holds: "Parent 2 home phone" },
      { name: "child.parents[1].cell", holds: "Parent 2 cell phone" },
      { name: "child.enrollment.scheduleTimeIn", holds: "Daily arrival time" },
      { name: "child.enrollment.scheduleTimeOut", holds: "Daily pickup time" },
      { name: "child.enrollment.meals.breakfast", holds: "Breakfast Y/N" },
      { name: "child.enrollment.meals.lunch", holds: "Lunch Y/N" },
      { name: "child.enrollment.meals.supper", holds: "Supper Y/N" },
      { name: "child.enrollment.breastMilk", holds: "Breast milk Y/N" },
      { name: "child.enrollment.formulaName", holds: "Formula brand name (if applicable)" },
      { name: "DATE", holds: "Year 1 signature date (Adobe's default DATE name)" },
      { name: "DATE_2", holds: "Year 2 renewal signature" },
      { name: "DATE_3", holds: "Year 3 renewal signature" },
      { name: "DATE_4", holds: "Year 4 renewal signature" },
    ],
    notes: [
      "The 4 DATE slots handle annual re-signing. Newest = compliance card 'Date on Form'.",
      "Adobe's default DATE / DATE_2 / DATE_3 / DATE_4 auto-numbering is used — no need to rename.",
    ],
  },
  financial_contract: {
    label: "Financial & Contract Agreement",
    fields: [
      { name: "child.financial.programType", holds: "Full-time / Part-time / other" },
      { name: "child.financial.agreedToSixMonthReview", holds: "Checkbox — agrees to 6-month review reminder" },
      { name: "child.forms.financial.signedDate", holds: "Date signed" },
    ],
    notes: [
      "signedDate starts the Financial Agreement renewal clock (interval configurable in Owner Tools).",
    ],
  },
  transportation_policy: {
    label: "School Age Transportation & Transfer of Care",
    fields: [
      { name: "child.schoolAge.school1.name", holds: "Primary school name" },
      { name: "child.schoolAge.school1.address", holds: "Primary school address" },
      { name: "child.schoolAge.school1.phone", holds: "Primary school phone" },
      { name: "child.schoolAge.school2.name", holds: "Secondary school (if any)" },
      { name: "child.schoolAge.school2.address", holds: "Secondary school address" },
      { name: "child.schoolAge.school2.phone", holds: "Secondary school phone" },
      { name: "child.schoolAge.pickupTimeToSchool", holds: "Center → School pickup time" },
      { name: "child.schoolAge.dropoffTimeFromSchool", holds: "School → Center dropoff time" },
      { name: "child.schoolAge.morningTransport", holds: "Y/N morning transport" },
      { name: "child.schoolAge.afternoonTransport", holds: "Y/N afternoon transport" },
      { name: "child.schoolAge.walkAuth.toSchool", holds: "Walk authorization: Center → School" },
      { name: "child.schoolAge.walkAuth.fromSchool", holds: "Walk authorization: School → Center" },
      { name: "child.schoolAge.walkAuth.toBusStop", holds: "Walk to bus stop" },
      { name: "child.schoolAge.walkAuth.fromBusStop", holds: "Walk from bus stop" },
      { name: "child.schoolAge.walkAuth.fromSchoolToCenter", holds: "Walk from school back to center" },
    ],
    notes: [
      "Applies only to children in School Age or After School categories. Won't appear on Form Status for other kids.",
    ],
  },
};

const NON_MAPPED_FORMS = [
  { label: "All About Your Child", why: "Free-form narrative — no structured data to extract." },
  { label: "Child Shot Records", why: "Usually a scanned document or state-issued form." },
  { label: "Child Health Report", why: "Usually a doctor-completed paper form." },
];

export const GUIDE_TABS = [
  { id: "start", label: "Getting Started" },
  { id: "emergency_consent", label: "Emergency Contact" },
  { id: "enrollment_info", label: "Enrollment Info" },
  { id: "financial_contract", label: "Financial" },
  { id: "transportation_policy", label: "Transportation" },
  { id: "pitfalls", label: "Common Pitfalls" },
  { id: "no_map", label: "Non-Mapped Forms" },
];

export function PdfFieldGuideBody({ tab, onTabChange, contentClassName = "" }) {
  return (
    <>
      <div className="border-b bg-white overflow-x-auto flex-shrink-0">
        <div className="flex gap-1 px-4 py-2 min-w-max">
          {GUIDE_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => onTabChange(t.id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                tab === t.id
                  ? "bg-teal-600 text-white"
                  : "text-gray-600 hover:bg-teal-50 hover:text-teal-700"
              }`}
              data-testid={`guide-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`text-sm text-gray-800 ${contentClassName}`}>
        {tab === "start" && <GettingStartedTab />}
        {tab === "pitfalls" && <PitfallsTab />}
        {tab === "no_map" && <NonMappedTab />}
        {PDF_FIELD_CATALOG[tab] && <FieldTable spec={PDF_FIELD_CATALOG[tab]} />}
      </div>
    </>
  );
}

export function PdfFieldGuideModal({ open, onClose, initialTab = "start" }) {
  const [tab, setTab] = useState(initialTab);
  React.useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="pdf-field-guide-modal">
        <DialogHeader className="px-6 pt-5 pb-3 border-b bg-gradient-to-r from-teal-50 to-cyan-50">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl flex items-center gap-2 text-teal-900">
                <FileText className="w-5 h-5" /> PDF Field Name Guide
              </DialogTitle>
              <p className="text-xs text-teal-700 mt-1">
                For centers building enrollment PDFs in Adobe Acrobat Pro · Guide v1.0
              </p>
            </div>
          </div>
        </DialogHeader>

        <PdfFieldGuideBody tab={tab} onTabChange={setTab} contentClassName="flex-1 overflow-y-auto px-6 py-4" />
      </DialogContent>
    </Dialog>
  );
}

const FieldTable = ({ spec }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-teal-900">{spec.label}</h3>
    <p className="text-xs text-gray-500 italic">
      Copy the exact field name (case, dots, and brackets matter) into the <strong>Name</strong> box of each field&apos;s Properties in Adobe Acrobat Pro.
    </p>
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-teal-50 text-teal-900 text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2 w-1/2">Adobe field name (exact)</th>
            <th className="text-left px-3 py-2">What it holds</th>
          </tr>
        </thead>
        <tbody>
          {spec.fields.map((f, i) => (
            <tr key={f.name} className={i % 2 ? "bg-gray-50" : "bg-white"}>
              <td className="px-3 py-1.5 font-mono text-xs text-teal-800 break-all">{f.name}</td>
              <td className="px-3 py-1.5 text-gray-700">{f.holds}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {spec.notes?.length > 0 && (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="font-semibold text-amber-900 mb-1 text-xs uppercase">💡 Notes</div>
        <ul className="list-disc pl-5 space-y-1 text-xs text-amber-900">
          {spec.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>
    )}
  </div>
);

const GettingStartedTab = () => (
  <div className="space-y-5">
    <div>
      <h3 className="text-lg font-semibold text-teal-900 mb-2">Why field names matter</h3>
      <p className="text-sm text-gray-700 leading-relaxed">
        When a parent uploads a filled-in PDF, this app reads the <strong>Adobe AcroForm field names</strong> to auto-fill the child record (parents, allergies, physician, insurance, etc.). If your field names don&apos;t match this guide, the PDF is still stored — but nothing auto-populates on the Edit Child page. Match the names in this guide and your data flows automatically.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-teal-900 mb-2">Step-by-step in Adobe Acrobat Pro</h3>
      <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
        <li>Open your PDF in Adobe Acrobat Pro.</li>
        <li>Click <strong>Tools → Prepare Form</strong>. If Acrobat prompts to auto-detect fields, click <strong>Skip</strong> (or let it, then rename as needed).</li>
        <li>Click the <strong>Text Field</strong> icon in the toolbar → click on your PDF where the field should appear.</li>
        <li>Right-click the new field → <strong>Properties</strong>.</li>
        <li>In the <strong>General</strong> tab → <strong>Name</strong> box, paste the exact field name from this guide.</li>
        <li>Click <strong>Close</strong>, save the PDF, and upload it in the Blank Form Templates section.</li>
      </ol>
    </div>

    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="font-semibold text-blue-900 mb-2 text-sm">💡 No Adobe Acrobat Pro?</div>
      <p className="text-sm text-blue-800 leading-relaxed">
        Free tools that support named AcroForm fields:
      </p>
      <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-blue-800">
        <li><a href="https://www.pdfescape.com" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">PDFescape <ExternalLink className="w-3 h-3" /></a> (web-based, free)</li>
        <li><a href="https://www.docfly.com" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">DocFly <ExternalLink className="w-3 h-3" /></a> (web, free tier)</li>
        <li>LibreOffice Draw (desktop, open-source)</li>
      </ul>
      <p className="text-xs text-blue-700 italic mt-2">
        Any tool that produces an interactive AcroForm PDF works. Avoid &quot;flat&quot; or &quot;static&quot; PDFs — those have no fields to read.
      </p>
    </div>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="font-semibold text-gray-800 mb-1 text-sm">🔄 What if I already built PDFs without these names?</div>
      <p className="text-sm text-gray-700">
        Your existing PDFs will still work as attachments — parents can fill and submit them. You&apos;ll just need to type the child&apos;s info manually into the Edit Child page. To enable auto-fill, rename each field to match this guide.
      </p>
    </div>
  </div>
);

const PitfallsTab = () => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-teal-900 flex items-center gap-2">
      <AlertTriangle className="w-5 h-5 text-amber-600" /> Common Pitfalls
    </h3>
    <ul className="space-y-3 text-sm">
      <li className="bg-red-50 border border-red-200 rounded p-3">
        <div className="font-semibold text-red-900">Case sensitivity</div>
        <p className="text-red-800 text-xs mt-1"><code className="bg-white px-1">Child.Mother.Name</code> will NOT match <code className="bg-white px-1">child.mother.name</code>. Always use lowercase exactly as shown.</p>
      </li>
      <li className="bg-red-50 border border-red-200 rounded p-3">
        <div className="font-semibold text-red-900">Trailing/leading spaces</div>
        <p className="text-red-800 text-xs mt-1">Adobe silently trims some, keeps others. Paste-then-verify by clicking away and back into the Name box.</p>
      </li>
      <li className="bg-red-50 border border-red-200 rounded p-3">
        <div className="font-semibold text-red-900">Duplicate auto-numbering</div>
        <p className="text-red-800 text-xs mt-1">If Adobe renames a duplicate as <code className="bg-white px-1">child.physician.name.1</code>, that won&apos;t match. Merge duplicates or delete extras.</p>
      </li>
      <li className="bg-red-50 border border-red-200 rounded p-3">
        <div className="font-semibold text-red-900">Signature vs Text field</div>
        <p className="text-red-800 text-xs mt-1">Use plain <strong>Text</strong> fields (not &quot;Signature&quot; fields) — the extractor reads text content, not signature dictionaries.</p>
      </li>
      <li className="bg-red-50 border border-red-200 rounded p-3">
        <div className="font-semibold text-red-900">Field grouping</div>
        <p className="text-red-800 text-xs mt-1">If Acrobat suggests grouping related fields, decline. Use flat names exactly as shown in the guide.</p>
      </li>
      <li className="bg-red-50 border border-red-200 rounded p-3">
        <div className="font-semibold text-red-900">Flat PDF (no fields)</div>
        <p className="text-red-800 text-xs mt-1">If your PDF has no AcroForm fields, nothing extracts. Verify in Acrobat: <strong>Tools → Prepare Form</strong> should show a list of fields. If empty, add them.</p>
      </li>
    </ul>
  </div>
);

const NonMappedTab = () => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-teal-900">Forms with no auto-mapping</h3>
    <p className="text-sm text-gray-700">These forms are stored as attachments and mark the packet form &quot;received&quot; — but no field extraction runs. Design them however you like.</p>
    <div className="space-y-2">
      {NON_MAPPED_FORMS.map(f => (
        <div key={f.label} className="border rounded-lg p-3 bg-gray-50">
          <div className="font-semibold text-gray-800 text-sm">{f.label}</div>
          <div className="text-xs text-gray-600 mt-1">{f.why}</div>
        </div>
      ))}
    </div>
  </div>
);

export default PdfFieldGuideModal;
